import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { getTimeEntries, getLastSevenDaysTimeEntries, getLastThirtyDaysTimeEntries, getEventsByUser, getEventEmployees } from '../../services/supabase';
import { supabase } from '../../services/supabase';
import { CalendarDays, Clock } from 'lucide-react';
import EventCard from '../../components/Events/EventCard'; // Add this import
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { PrimaryButton, SecondaryButton } from '../../components/Common/BaseButton';
import AddNewEventButton  from '../../components/ManagerPermission/AddNewEventButton';
import { useRoleContext } from '../../context/RoleContext';
import { DateTime } from 'luxon';

const DashboardContainer = styled.div`
  padding: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const WelcomeCard = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  /* margin-bottom: 1.5rem; // Removed as TopBar will handle spacing */
  flex-grow: 1; /* Allow welcome card to take available space */
`;

const WelcomeTitle = styled.h2`
  font-size: 1.5rem;
  color: var(--primary-green);
  margin-top: 0;
  margin-bottom: 0.5rem;
`;

const WelcomeCardContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Grid = styled.div`
  display: grid;
  /* grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); */
  grid-template-columns: 2fr 1fr; /* Default to 2/3 and 1/3 for desktop */
  gap: 1.5rem;
  
  @media (max-width: 1024px) { /* Adjust breakpoint as needed */
    grid-template-columns: 1fr; /* Stack on smaller screens / tablets */
  }
`;

const SectionCard = styled.div` /* Renamed from Card for clarity */
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  height: min-content;
  
  h3 {
    margin-top: 0;
    color: #333;
    display: flex;
    align-items: center;
  }
`;

const EventCountBadge = styled.span`
  background-color: #e0e0e0;
  color: #333;
  margin-left: 0.5rem;
  padding: 0.25rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: normal;
`;


const Stat = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0; /* Increased padding slightly */
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const StatLabel = styled.span`
  font-weight: 500;
  color: #555; /* Slightly softer color */
  font-size: 0.9rem;
`;

const StatValue = styled.span`
  font-weight: 700;
  color: var(--primary-green);
  font-size: 0.9rem;
`;

const TimeTrackingStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const StatusText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.9rem;
  font-weight: 500;
  background-color: color-mix(in srgb, ${props => props.$color} 12%, white);
  color: ${props => props.$color};
`;

const formatDateRange = (days) => {
  const end = DateTime.now();
  const start = end.minus({ days: days - 1 }); // Adjust to match common "last X days including today"
  
  return `${start.toFormat('MMM d')} - ${end.toFormat('MMM d')}`;
};

const calculateTotalHours = (timeEntries) => {
  if (!timeEntries || timeEntries.length === 0) return '0h 0m';
  
  const totalMinutes = timeEntries.reduce((total, entry) => {
    return total + (entry.duration_minutes || 0);
  }, 0);

  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  
  return `${hours}h ${minutes}m`;
};

const DashboardPage = () => {
  const { user } = useAuth();
  const { isManager, isAuthenticated } = useRoleContext();
  const [timeEntries, setTimeEntries] = useState([]);
  const [last7DaysEntries, setLast7DaysEntries] = useState([]);
  const [last30DaysEntries, setLast30DaysEntries] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventEmployeesMap, setEventEmployeesMap] = useState({});
  const [error, setError] = useState(null);
  const [currentTimeTrackingStatus, setCurrentTimeTrackingStatus] = useState("Not Tracking");
  const [currentTimeTrackingStatusColor, setCurrentTimeTrackingStatusColor] = useState("#555");

  useEffect(() => {
    const fetchData = async () => {
      if (user && user.id) {
        setLoading(true);
        setError(null);
        try {
          // Fetch time entries
          const [
            timeDataRes, 
            last7DaysRes, 
            last30DaysRes, 
            eventsDataRes
          ] = await Promise.all([
            getTimeEntries(user.id),
            getLastSevenDaysTimeEntries(user.id),
            getLastThirtyDaysTimeEntries(user.id),
            getEventsByUser(user.id)
          ]);

          if (timeDataRes.data) setTimeEntries(timeDataRes.data);
          if (last7DaysRes.data) setLast7DaysEntries(last7DaysRes.data);
          if (last30DaysRes.data) setLast30DaysEntries(last30DaysRes.data);

          if (eventsDataRes.data) {
            const allUserEvents = eventsDataRes.data.map(item => item.event).filter(event => event);
            
            // Get today's date in YYYY-MM-DD format
            const now = DateTime.now();
            const today = now.toFormat('yyyy-MM-dd');
            
            // Filter for upcoming events
            const filteredUpcomingEvents = allUserEvents
            .filter(event => {
              const eventDateTime = DateTime.fromISO(event.start_time).setZone(event.timezone);
              return eventDateTime >= now.startOf('day');
            })
            .sort((a, b) => {
              const timeA = DateTime.fromISO(a.start_time).setZone(a.timezone);
              const timeB = DateTime.fromISO(b.start_time).setZone(b.timezone);
              return timeA.toMillis() - timeB.toMillis();
            });

            setUpcomingEvents(filteredUpcomingEvents);

            if (filteredUpcomingEvents.length > 0) {
              const employeePromises = filteredUpcomingEvents.map(event => getEventEmployees(event.id));
              const employeeResults = await Promise.all(employeePromises);
              
              const newEventEmployeesMap = {};
              employeeResults.forEach((result, index) => {
                if (result.data) {
                  newEventEmployeesMap[filteredUpcomingEvents[index].id] = result.data;
                } else if (result.error) {
                  console.warn(`Error fetching employees for event ${filteredUpcomingEvents[index].id}:`, result.error);
                  newEventEmployeesMap[filteredUpcomingEvents[index].id] = []; // Set empty array on error
                }
              });
              setEventEmployeesMap(newEventEmployeesMap);
            }
          }
        } catch (err) {
          console.error("Failed to fetch dashboard data:", err);
          setError("Failed to load some dashboard data. Please try again.");
        } finally {
          setLoading(false);
        }
      } else if (!user) {
        setLoading(false); // Not logged in, stop loading
      }
    };
    
    fetchData();
  }, [user]);

  useEffect(() => {
    // This useEffect for Google Access Token seems unrelated to dashboard display, keeping as is.
    const handleRedirect = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error('Error getting session:', error);
        return;
      }
      if (data.session && data.session.provider_token) {
        localStorage.setItem('googleAccessToken', data.session.provider_token);
      }
    };

    handleRedirect();
  }, []);

  useEffect(() => {
    if (user && user.id) {
      const STORAGE_KEY = `timeEntry_${user.id}`;
      try {
        const savedData = localStorage.getItem(STORAGE_KEY);
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          if (parsedData.isClockedIn) {
            setCurrentTimeTrackingStatus("Clocked In");
            setCurrentTimeTrackingStatusColor("var(--primary-green)");
          } else {
            setCurrentTimeTrackingStatus("Not Tracking"); 
            setCurrentTimeTrackingStatusColor("#555");
          }
        } else {
          // No saved data in localStorage implies not clocked in
          // via the TimeEntryForm's active session mechanism.
          setCurrentTimeTrackingStatus("Not Tracking");
          setCurrentTimeTrackingStatusColor("#555");
        }
      } catch (err) {
        console.error('Error reading time entry state from local storage:', err);
        setCurrentTimeTrackingStatus("Not Tracking (Error)");
        setCurrentTimeTrackingStatusColor("red");
      }
    } else {
      // No user, so not tracking
      setCurrentTimeTrackingStatus("Not Tracking");
      setCurrentTimeTrackingStatusColor("#555");
    }
  }, [timeEntries, loading, error, user]); // Added loading, error, user to dependencies

  if (loading) {
    return (
      <LoadingSpinner text="Loading Dashboard..." />
    );
  }
  if (error) {
    return <DashboardContainer><p style={{color: 'red'}}>{error}</p></DashboardContainer>;
  }
  if (!user) {
    return <DashboardContainer>Please log in to view the dashboard.</DashboardContainer>;
  }

  const hoursLast7Days = calculateTotalHours(last7DaysEntries);
  const hoursLast30Days = calculateTotalHours(last30DaysEntries);
  const totalHours = calculateTotalHours(timeEntries);
  return (
    <DashboardContainer>
      <TopBar>
        <WelcomeCard>
          <WelcomeCardContent>
            <div>
            <WelcomeTitle>Welcome back, {user.user_metadata?.full_name?.split(' ')[0] || 'User'}</WelcomeTitle>
            <p>Here's your activity summary and quick actions.</p>
            </div>
            <AddNewEventButton 
              onClick={() => window.location.href = '/events/new'}
              isManager={isManager}
              isAuthenticated={isAuthenticated}
            />
          </WelcomeCardContent>
        </WelcomeCard>
      </TopBar>
      
      <Grid>
        <SectionCard>
          <h3>
            Upcoming Events
            {upcomingEvents.length > 0 && <EventCountBadge>{upcomingEvents.length} Event{upcomingEvents.length > 1 ? 's' : ''}</EventCountBadge>}
          </h3>
          {upcomingEvents.length === 0 ? (
            <div>
              <p>No upcoming events assigned to you yet.</p>
              <SecondaryButton
                onClick={() => window.location.href = '/events?view=calendar'}
              >
                <CalendarDays size={14} /> Calendar View
              </SecondaryButton>
            </div>
          ) : (
            <>
              <div>
                {upcomingEvents.slice(0, 3).map(event => {
                  const employees = eventEmployeesMap[event.id] || [];
                  return (
                    <EventCard 
                      key={event.id} 
                      event={event} 
                      eventEmployees={employees} 
                      isManager={isManager}
                    />
                  );
                })}
              </div>
              <div style={{display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1rem'}}>
                {upcomingEvents.length > 3 && ( 
                  <SecondaryButton
                    onClick={() => window.location.href = '/events'}
                  >
                    View All Events
                  </SecondaryButton>
                )}
                <SecondaryButton
                  onClick={() => window.location.href = '/events?view=calendar'}
                >
                  <CalendarDays size={14} /> Calendar View
                </SecondaryButton>
              </div>
            </>
          )}
        </SectionCard>
        
        <SectionCard>
          <h3>Time Tracking Summary</h3>
          <Stat>
            <StatLabel>Last 7 days ({formatDateRange(7)})</StatLabel>
            <StatValue>{hoursLast7Days}</StatValue>
          </Stat>
          <Stat>
            <StatLabel>Last 30 days ({formatDateRange(30)})</StatLabel>
            <StatValue>{hoursLast30Days}</StatValue>
          </Stat>
          <Stat>
            <StatLabel>All time</StatLabel>
            <StatValue>{totalHours}</StatValue>
          </Stat>
          <TimeTrackingStatus>
            <StatusText>
              <Clock size={16} /> 
              <span>Current Status:</span>
            </StatusText>
            <StatusBadge $color={currentTimeTrackingStatusColor}>
              {currentTimeTrackingStatus}
            </StatusBadge>
          </TimeTrackingStatus>
          <PrimaryButton style={{marginTop: '1rem', width: '100%'}} onClick={() => window.location.href = '/time-tracking'}>Log Time Now</PrimaryButton>
        </SectionCard>
        
      </Grid>
    </DashboardContainer>
  );
};

export default DashboardPage;
